/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './ResidentialAddress.scss';

import { func, object } from 'prop-types';
import React from 'react';

import { ReactComponent as WarningCircleIcon } from 'assets/icons/warning-circle.svg';
import AddressTypeSelect from 'components/layouts/Preferences/UpdateContactInformation/MailingAddress/AddressTypeSelect';
import MilitaryAddress from 'components/layouts/Preferences/UpdateContactInformation/MailingAddress/MilitaryAddress';
import NonUSAddress from 'components/layouts/Preferences/UpdateContactInformation/MailingAddress/NonUSAddress';
import PreferencesDataRow from 'components/layouts/Preferences/shared/PreferencesDataRow';
import PreferencesDataRowLabel from 'components/layouts/Preferences/shared/PreferencesDataRowLabel';
import PreferencesDataRowValue from 'components/layouts/Preferences/shared/PreferencesDataRowValue';
import PreferencesCol from 'components/layouts/Preferences/shared/PreferencesCol';
import PreferencesRow from 'components/layouts/Preferences/shared/PreferencesRow';
import PreferencesRowLabel from 'components/layouts/Preferences/shared/PreferencesRowLabel';
import USAddress from 'components/layouts/Preferences/UpdateContactInformation/MailingAddress/USAddress';
import BaseModalButton from 'components/shared/Modal/BaseModalButton';
import Content from 'services/Content';
import { residentialAddressValidation } from 'services/Validation';
import DisclosureModal from 'components/shared/Disclosure/DisclosureModal';
import { ReactComponent as QuestionCircle } from 'assets/icons/question-circle.svg';
import { getWarningTextContent } from 'components/layouts/Preferences/UpdateContactInformation/PreferencesUpdateContactInformation.js';
import { residentialAddressEnabled } from 'services/FeatureFlags';

const ResidentialAddress = ({
  address,
  onChange,
  hasResidentialAddress,
  owns529Only,
  ownsBrokerage,
  mailingAddress
}) => {
  const { addressType } = address;
  let AddressComponent = USAddress;
  const mailingAddressType = mailingAddress && mailingAddress.addressType;
  if (addressType === 'military') {
    AddressComponent = MilitaryAddress;
  } else if (addressType === 'non-us') {
    AddressComponent = NonUSAddress;
  }

  const onAddressChange = (field, value) => onChange('address', field)(value);

  const warningTextContent = getWarningTextContent(addressType, owns529Only, ownsBrokerage);
  return (
    <PreferencesRow className="mailing-address-section">
      <PreferencesCol className="mailing-address-title" col={3} mobile={12}>
        <div>
          <div className="mailing-address">
            <PreferencesRowLabel label="Residential/Legal Address" />
            <DisclosureModal
              className="restricted-height-modal"
              title="Residential/Legal Address"
              icon={<QuestionCircle className="information-modal-icon" />}
              content={ residentialAddressEnabled() ? Content.residentialAddressEnabledresidentialAddressToolTipCopy : Content.residentialAddressToolTipCopy}
              hideLinkContent
            />
          </div>
          <div
            className="mailing-address-copy-label"
            data-testid="mailing-address-copy-label">
            <span
              dangerouslySetInnerHTML={residentialAddressEnabled() ? { __html: Content.residentialAddressEnabledResidentialAddressCopy } : { __html: Content.residentialAddressCopy }}
            />
          </div>
        </div>
        <BaseModalButton
          buttonClassName="question-icon"
          label={<WarningCircleIcon />}
          title="Residential/Legal address">
          <span
            className="warning-text"
            dangerouslySetInnerHTML={{ __html: warningTextContent }}
          />
        </BaseModalButton>
      </PreferencesCol>
      <PreferencesCol
        col={6}
        mobile={12}
        className="editable-preferences-section">
        <PreferencesDataRow className="address-type-select">
          <PreferencesDataRowLabel>Address Type</PreferencesDataRowLabel>
          <PreferencesDataRowValue>
            <AddressTypeSelect
              onChange={onAddressChange}
              selected={addressType}
              mailingType="residential"
            />
          </PreferencesDataRowValue>
        </PreferencesDataRow>
        <AddressComponent
          address={address}
          onChange={onAddressChange}
          mailingType={'residential'}
          hasResidential={hasResidentialAddress}
          validations={residentialAddressValidation}
        />
      </PreferencesCol>
      {(addressType === 'non-us' || mailingAddressType==='non-us') &&
      (<PreferencesCol col={3} mobile={12}>
        <div className="warning">
          <WarningCircleIcon className="warning-icon" />
          <span
            className="warning-text"
            dangerouslySetInnerHTML={{ __html: warningTextContent }}
          />
        </div>
      </PreferencesCol>)}
    </PreferencesRow>
  );
};

ResidentialAddress.propTypes = {
  address: object.isRequired,
  onChange: func.isRequired
};


export default ResidentialAddress;
